import { Memory, ContentCopy, Timer, AutoMode } from '@mui/icons-material';
import {
    Alert,
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Switch,
    FormControlLabel,
    Chip
} from '@mui/material';
import { Node } from '../k8s-analyzer-types';
import { calculatePercentage, formatMemory, parseMemoryValue } from './k8s-analyzer-nodes-list.utils';
import { useSendSIGTERMtoPodActionMutation } from '../../../../common/reducers/backend-api/backend-k8s-api';
import { useEffect, useState, useCallback } from 'react';

const COOLDOWN_DURATION = 20 * 60 * 1000; // 20 minutes in milliseconds
const AUTO_SIGTERM_INTERVAL = 5 * 60 * 1000; // 3 minutes in milliseconds
const LOCAL_STORAGE_KEY = 'node-sigterm-cooldowns';
const AUTO_MODE_KEY = 'node-sigterm-auto-mode';
const LAST_AUTO_SIGTERM_KEY = 'last-auto-sigterm-timestamp';

interface HighMemoryAlert {
    nodeName: string;
    nodeMemoryPercent: number;
    podName: string;
    podMemoryUsage: string;
}

interface NodeCooldown {
    timestamp: number;
    podName: string;
}

// Utility functions for managing cooldowns
const getCooldowns = (): Record<string, NodeCooldown> => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
};

const setCooldown = (nodeName: string, podName: string) => {
    const cooldowns = getCooldowns();
    cooldowns[nodeName] = {
        timestamp: Date.now(),
        podName
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cooldowns));
};

const isNodeInCooldown = (nodeName: string): boolean => {
    const cooldowns = getCooldowns();
    const cooldown = cooldowns[nodeName];
    if (!cooldown) return false;
    
    const timeElapsed = Date.now() - cooldown.timestamp;
    return timeElapsed < COOLDOWN_DURATION;
};

const getTimeRemaining = (timestamp: number, duration: number): string => {
    const timeElapsed = Date.now() - timestamp;
    const remainingMs = Math.max(0, duration - timeElapsed);
    const minutes = Math.floor(remainingMs / 60000);
    const seconds = Math.floor((remainingMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
};

const NodeCooldownList = ({ cooldowns }: { cooldowns: Record<string, NodeCooldown> }) => {
    const activeCooldowns = Object.entries(cooldowns).filter(
        ([, { timestamp }]) => Date.now() - timestamp < COOLDOWN_DURATION
    );

    if (activeCooldowns.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No nodes currently in cooldown
            </Typography>
        );
    }

    return (
        <List dense>
            {activeCooldowns.map(([nodeName, { timestamp, podName }]) => (
                <ListItem key={nodeName}>
                    <ListItemIcon>
                        <Timer color="action" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Typography variant="body2">
                                {nodeName}
                            </Typography>
                        }
                        secondary={
                            <Typography variant="caption" color="text.secondary">
                                Last SIGTERM to pod: {podName}
                                <br />
                                Time remaining: {getTimeRemaining(timestamp, COOLDOWN_DURATION)}
                            </Typography>
                        }
                    />
                </ListItem>
            ))}
        </List>
    );
};

export function K8sAnalyzerHighMemoryAlert({ nodes }: { nodes: Node[] }) {
    const [sendSIGTERMtoPod, { isLoading }] = useSendSIGTERMtoPodActionMutation();
    const [cooldowns, setCooldowns] = useState<Record<string, NodeCooldown>>({});
    const [autoMode, setAutoMode] = useState(() => {
        return localStorage.getItem(AUTO_MODE_KEY) === 'true';
    });
    const [lastAutoSigterm, setLastAutoSigterm] = useState(() => {
        const stored = localStorage.getItem(LAST_AUTO_SIGTERM_KEY);
        return stored ? parseInt(stored, 10) : Date.now(); // Initialize with current time if no stored value
    });
    const [, setRefreshKey] = useState(0);

    // Update lastAutoSigterm when autoMode is enabled
    useEffect(() => {
        if (autoMode) {
            const stored = localStorage.getItem(LAST_AUTO_SIGTERM_KEY);
            if (!stored) {
                const now = Date.now();
                localStorage.setItem(LAST_AUTO_SIGTERM_KEY, now.toString());
                setLastAutoSigterm(now);
            }
        }
    }, [autoMode]);

    // Find node with highest memory consumption above 90%
    let highestMemNode: HighMemoryAlert | null = null;

    // Filter out nodes in cooldown and sort remaining nodes by memory percentage
    const availableNodes = nodes.filter(node => !isNodeInCooldown(node.name));

    availableNodes.forEach(node => {
        const memPercentage = calculatePercentage(node.metrics.memory.usage, node.metrics.memory.capacity, 'memory');

        if (memPercentage >= 90) {
            // Find pod with highest memory usage above 3000Mi in this node
            let highestMemPod: { name: string; usage: string } | null = null;

            node.pods_list?.forEach(pod => {
                // Only consider pods that are in "Running" status and have high memory usage
                if (pod.status === "Running") {
                    const podMemUsage = parseMemoryValue(pod.metrics.memory.usage);
                    if (podMemUsage >= 3000) {  // 3000Mi threshold
                        if (!highestMemPod || parseMemoryValue(pod.metrics.memory.usage) > parseMemoryValue(highestMemPod.usage)) {
                            highestMemPod = { name: pod.name, usage: pod.metrics.memory.usage };
                        }
                    }
                }
            });

            if (!highestMemNode || memPercentage > highestMemNode.nodeMemoryPercent) {
                if (highestMemPod) {
                    highestMemNode = {
                        nodeName: node.name,
                        nodeMemoryPercent: memPercentage,
                        podName: highestMemPod.name,
                        podMemoryUsage: highestMemPod.usage
                    };
                }
            }
        }
    });

    const handleSendSIGTERM = useCallback(async (podName: string, nodeName: string) => {
        console.log(`[SIGTERM] Sending SIGTERM to pod ${podName} on node ${nodeName}`);
        if (!podName) {
            console.log(`[SIGTERM] No pod name provided for node ${nodeName}`);
            return;
        }

        await sendSIGTERMtoPod(podName);
        setCooldown(nodeName, podName);
        const now = Date.now();
        localStorage.setItem(LAST_AUTO_SIGTERM_KEY, now.toString());
        setLastAutoSigterm(now);
        setCooldowns(getCooldowns());
    }, [sendSIGTERMtoPod]);

    // Update cooldowns and force re-render every second
    useEffect(() => {
        setCooldowns(getCooldowns());
        const interval = setInterval(() => {
            setCooldowns(getCooldowns());
            setRefreshKey(key => key + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Auto mode effect
    useEffect(() => {
        localStorage.setItem(AUTO_MODE_KEY, autoMode.toString());
        let intervalId: NodeJS.Timeout;

        if (autoMode) {
            const checkAndExecute = async () => {
                const now = Date.now();
                const timeSinceLastSigterm = now - lastAutoSigterm;

                if (timeSinceLastSigterm >= AUTO_SIGTERM_INTERVAL && highestMemNode) {
                    console.log(`[AUTO-SIGTERM] Interval elapsed (${AUTO_SIGTERM_INTERVAL}ms), executing SIGTERM`);
                    await handleSendSIGTERM(highestMemNode.podName, highestMemNode.nodeName);
                } else if (highestMemNode) {
                    // If we have a node but haven't hit the interval, ensure timer is running
                    if (timeSinceLastSigterm < 0) {
                        // If lastAutoSigterm is in the future, reset it
                        const now = Date.now();
                        localStorage.setItem(LAST_AUTO_SIGTERM_KEY, now.toString());
                        setLastAutoSigterm(now);
                    }
                } else {
                    // No high memory node, reset the timer if it's expired
                    if (timeSinceLastSigterm >= AUTO_SIGTERM_INTERVAL) {
                        const now = Date.now();
                        localStorage.setItem(LAST_AUTO_SIGTERM_KEY, now.toString());
                        setLastAutoSigterm(now);
                    }
                }
            };
            
            // Initial check when panel appears
            checkAndExecute();
            
            // Set up interval
            intervalId = setInterval(checkAndExecute, 5000); // Check every 5 seconds
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [autoMode, lastAutoSigterm, handleSendSIGTERM]);

    const nextAutoSigtermTime = lastAutoSigterm + AUTO_SIGTERM_INTERVAL;
    const showNextAutoSigterm = autoMode && Date.now() < nextAutoSigtermTime;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Node Cooldown Status Section */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                        Node Cooldown Status
                    </Typography>
                </Box>
                <NodeCooldownList cooldowns={cooldowns} />
            </Paper>

            {/* High Memory Alert Section - Always Visible */}
            <Alert
                severity={highestMemNode ? "warning" : "info"}
                icon={<Memory />}
                sx={{
                    '& .MuiAlert-message': {
                        width: '100%'
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        High Memory Usage Monitor
                    </Typography>
                    
                    {highestMemNode ? (
                        <>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2">
                                    Node <strong>{highestMemNode.nodeName}</strong> is at <strong>{Math.round(highestMemNode.nodeMemoryPercent)}%</strong> memory capacity
                                </Typography>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Pod <strong>{highestMemNode.podName}</strong>
                                    <Tooltip title="Copy pod name">
                                        <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(highestMemNode.podName)}
                                            sx={{ padding: 0.5 }}
                                        >
                                            <ContentCopy fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    is using <strong>{formatMemory(highestMemNode.podMemoryUsage)}</strong> of memory
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button 
                                    variant="contained" 
                                    color="error" 
                                    onClick={() => handleSendSIGTERM(highestMemNode.podName, highestMemNode.nodeName)}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Sending SIGTERM...' : 'Send SIGTERM'}
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Typography variant="body2">
                            No pods currently exceeding high memory threshold (3000Mi) on nodes with high memory usage (90%+)
                        </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoMode}
                                    onChange={(e) => setAutoMode(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AutoMode fontSize="small" />
                                    <Typography variant="body2">Auto Mode</Typography>
                                </Box>
                            }
                        />
                        {showNextAutoSigterm && (
                            <Chip
                                icon={<Timer />}
                                label={`Next auto SIGTERM in: ${getTimeRemaining(lastAutoSigterm, AUTO_SIGTERM_INTERVAL)}`}
                                color="info"
                                variant="outlined"
                                size="small"
                            />
                        )}
                    </Box>
                </Box>
            </Alert>
        </Box>
    );
} 